/**
 * The roles this cabinet may hand out inside a company, read from the server's own
 * catalogue instead of from a list typed into the app.
 *
 * The app used to carry `['admin', 'accountant']` as a constant. Two things were wrong
 * with that. A role the platform adds — and the catalogue is a table, so it will —
 * stays invisible until the app ships again. And the labels were translated here, in
 * three locale files, while `RoleDefinitionInfo` already carries `name` and
 * `description` as locale maps: two sources for one string, drifting apart quietly.
 *
 * `GET /roles/definitions` costs nothing to ask — no Casbin check at all, reference
 * data for any authenticated caller — so it is fetched once and held for the session.
 *
 * **The hard-coded list survives as a FALLBACK, not as the source.** If the catalogue
 * cannot be reached (offline, or a gateway older than the route), the screen still
 * offers the two roles that have existed all along rather than showing an owner an
 * empty row of chips and no way to appoint anybody.
 */
import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchRoleDefinitions} from '@/entities/role/api';
import {
  DOMAIN_TYPE_COMPANY,
  domainTypeOf,
  roleDescription,
  roleLabel,
  type RoleDefinition,
} from '@/entities/role/types';

/** One offerable role, already resolved into the strings a chip needs. */
export type CompanyRoleOption = {
  code: string;
  label: string;
  description: string;
};

/**
 * Codes the cabinet never offers, whatever the catalogue says about them.
 *
 * `owner` is the whole list. A company's owner is `owner_account_id` on the company
 * row — one account, set at creation and changed by transferring the company — and the
 * Casbin role of the same name is what mirrors it. Handing it out from a chip would
 * make a second owner without the transfer that is supposed to accompany one, and the
 * screen already refuses to REMOVE an owner for the mirror-image reason.
 */
const NEVER_OFFERED = new Set(['owner']);

/** Used only when the catalogue cannot be read — see the module note. */
const FALLBACK_CODES = ['admin', 'accountant'];

export type CompanyRolesResult = {
  /** What the role chips offer, in catalogue priority order. */
  options: CompanyRoleOption[];
  /** Every company-scoped definition, assignable or not — for reading a held role. */
  definitions: RoleDefinition[];
  /** The display name for a code, including codes not offered (`owner`). */
  labelFor: (code: string) => string;
  isPending: boolean;
  /** True while the fallback list is standing in for the real catalogue. */
  isFallback: boolean;
};

export function useCompanyRoles(): CompanyRolesResult {
  const {t, i18n} = useTranslation();

  const query = useQuery({
    queryKey: ['role-definitions'],
    // Asked for EVERY domain and filtered below rather than with `?domain_type=1`:
    // the field comes back as an int from one route and a name from another, and
    // `domainTypeOf` copes with both where a server-side filter would just return
    // nothing if this app guessed the spelling wrong.
    queryFn: () => fetchRoleDefinitions(),
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  return useMemo(() => {
    const all = query.data ?? [];
    const companyScoped = all.filter(
      d => domainTypeOf(d.domain_type) === DOMAIN_TYPE_COMPANY,
    );

    const labelFor = (code: string): string => {
      const def = companyScoped.find(d => d.code === code);
      // `defaultValue: code` matters: a role the catalogue grew after this build must
      // still read as something a human can quote back to support.
      return def ? roleLabel(def, i18n.language) : t(`members.role.${code}`, code);
    };

    const isFallback = !query.data && !query.isPending;

    const options: CompanyRoleOption[] = isFallback
      ? FALLBACK_CODES.map(code => ({
          code,
          label: t(`members.role.${code}`, code),
          description: '',
        }))
      : companyScoped
          .filter(d => d.is_assignable && !NEVER_OFFERED.has(d.code))
          .slice()
          // Catalogue priority, then code, so the order is the platform's and not
          // whatever order the rows happened to arrive in.
          .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0) || a.code.localeCompare(b.code))
          .map(d => ({
            code: d.code,
            label: roleLabel(d, i18n.language),
            description: roleDescription(d, i18n.language),
          }));

    return {
      options,
      definitions: companyScoped,
      labelFor,
      isPending: query.isPending,
      isFallback,
    };
  }, [query.data, query.isPending, i18n.language, t]);
}
